import { SYSTEM_LAYER_NAME_SET } from '@designdeck/shared';

/** Mirrors the caps enforced by the API DTO. */
export const MAX_TEXTS_PER_POSITION = 20;
export const MAX_TEXT_LENGTH = 500;

/** Serialised (`IText`, `Textbox`, `Text`) and live (`i-text`, `textbox`, `text`) type names, lowercased. */
const TEXT_TYPES: ReadonlySet<string> = new Set([
  'text',
  'i-text',
  'itext',
  'textbox',
]);

/**
 * Collect the plain text of every text object in a persisted canvas JSON.
 *
 * Works on the serialised shape, not on live Fabric objects: by the Publish
 * step the editor is unmounted and only `masterPositions[key].json` is left.
 * Fabric serialises text classes as `IText`, `Textbox` or `Text` while the
 * live `type` getter lowercases them, so the match is case-insensitive
 * against both spellings. Groups are traversed, hidden objects and system layers are
 * skipped, whitespace is collapsed and the per-object and per-position caps
 * keep the prompt bounded.
 */
export function extractCanvasTexts(json: unknown): string[] {
  const texts: string[] = [];

  const visit = (node: unknown): void => {
    if (texts.length >= MAX_TEXTS_PER_POSITION) return;
    if (!node || typeof node !== 'object') return;

    const record = node as Record<string, unknown>;
    if (record.visible === false) return;
    if (
      typeof record.name === 'string' &&
      SYSTEM_LAYER_NAME_SET.has(record.name)
    ) {
      return;
    }

    const type =
      typeof record.type === 'string' ? record.type.toLowerCase() : '';
    if (TEXT_TYPES.has(type) && typeof record.text === 'string') {
      const text = record.text.replace(/\s+/g, ' ').trim();
      if (text) texts.push(text.slice(0, MAX_TEXT_LENGTH));
    }

    if (Array.isArray(record.objects)) {
      record.objects.forEach(visit);
    }
  };

  visit(json);
  return texts;
}
